import { BaseSelectorOption } from "@/types";
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  Box,
  CircularProgress,
  SxProps
} from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { Component, FocusEvent, SyntheticEvent } from "react";
import DropIcon from "../../resources/images/icons-chevron-double.svg";
import RemoveIcon from "../../resources/images/icons-remove.svg";
import { TextField } from "../TextField";

interface TrainingSelectorProps<TOption extends BaseSelectorOption = BaseSelectorOption> {
  label?: string;
  allowMultiple?: boolean;
  labelKey?: string;
  options: TOption[];
  value?: TOption | null;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    value: TOption,
    reason?: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>
  ) => void;
  onSearch?: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  sx?: SxProps;
  getOptionLabel?: (option: TOption) => string;
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
  translate?: (key: string) => string;
}

export class TrainingSelector<
  TOption extends BaseSelectorOption = BaseSelectorOption
> extends Component<TrainingSelectorProps<TOption>, any> {
  static defaultProps = {
    label: "Integration (required)",
    allowMultiple: false,
    labelKey: "name"
  };

  constructor(props: any) {
    super(props);
  }

  onChange(e: any, course: any, reason: AutocompleteChangeReason) {
    const { onChange } = this.props;

    if (reason === "removeOption" && e?.type !== "click") return;
    onChange?.(e, course);
  }

  onInputChange(e: any, value: string, reason: AutocompleteInputChangeReason) {
    const { onSearch } = this.props;

    if (reason === "input") {
      onSearch?.(value);
    }
  }

  onBlur(e: FocusEvent<HTMLDivElement>) {
    const { onBlur } = this.props;

    if (onBlur) onBlur(e);
  }

  renderOption(props: any, option: any, state: any) {
    const { labelKey } = this.props;

    const matches = match(
      option[labelKey || TrainingSelector.defaultProps.labelKey],
      state.inputValue,
      {
        insideWords: true,
        requireMatchAll: false
      }
    );
    const parts = parse(option[labelKey || TrainingSelector.defaultProps.labelKey], matches);

    return (
      <li {...props} key={option.value}>
        {parts.map((part, index) => (
          <span
            key={index}
            className={
              "klayo-selector_itempart" +
              (part.highlight === true ? " klayo-selector_highlight" : "")
            }
          >
            {part.text}
          </span>
        ))}
      </li>
    );
  }

  render() {
    const {
      label,
      allowMultiple,
      sx,
      placeholder,
      options,
      value,
      error,
      errorMessage,
      disabled,
      isLoading,
      labelKey
    } = this.props;

    return (
      <Autocomplete
        id='klayo-attributeselector'
        className='klayo-attributeselector klayo-selector'
        options={options}
        value={value || null}
        getOptionLabel={(option) => {
          return option[labelKey || TrainingSelector.defaultProps.labelKey];
        }}
        renderOption={this.renderOption.bind(this)}
        disabled={disabled}
        multiple={allowMultiple}
        popupIcon={<DropIcon />}
        clearIcon={<RemoveIcon />}
        ChipProps={{
          deleteIcon: <RemoveIcon />
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              value={value}
              label={label}
              placeholder={placeholder}
              disabled={disabled}
              error={error}
              helperText={errorMessage}
              sx={{ width: "100%" }}
              endAdornment={params.InputProps.endAdornment}
            />
          );
        }}
        onChange={this.onChange.bind(this)}
        onInputChange={this.onInputChange.bind(this)}
        onBlur={this.onBlur.bind(this)}
        sx={sx}
        loading={isLoading}
        loadingText={
          <Box display='flex' justifyContent={"center"} alignItems={"center"}>
            <CircularProgress size={20} />
          </Box>
        }
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return option.id === value.id;
        }}
        noOptionsText={this.props.translate?.("common.noOptions") || "No options"}
      />
    );
  }
}
